import type { ApiErrorEnvelope } from './types';

export interface ApiHttpError extends Error {
  code: string;
  details?: unknown;
  requestId?: string;
  statusCode: number;
}

interface NormalizeApiErrorProps {
  payload: unknown;
  statusCode: number;
  fallbackMessage: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const parseApiErrorEnvelope = (payload: unknown): ApiErrorEnvelope | null => {
  if (!isRecord(payload)) {
    return null;
  }

  const code = payload.code;
  const message = payload.message;
  const requestId = payload.requestId;

  if (typeof code !== 'string' || typeof message !== 'string') {
    return null;
  }

  return {
    code,
    message,
    details: payload.details,
    requestId: typeof requestId === 'string' ? requestId : undefined,
  };
};

export const normalizeApiError = ({
  payload,
  statusCode,
  fallbackMessage,
}: NormalizeApiErrorProps): ApiHttpError => {
  const envelope = parseApiErrorEnvelope(payload);

  const error = new Error(envelope?.message ?? fallbackMessage) as ApiHttpError;

  error.name = 'ApiHttpError';
  error.code = envelope?.code ?? 'http-error';
  error.statusCode = statusCode;

  if (typeof envelope?.details !== 'undefined') {
    error.details = envelope.details;
  }

  if (typeof envelope?.requestId === 'string') {
    error.requestId = envelope.requestId;
  }

  return error;
};
